import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const STEPS = ['submitted', 'under_review', 'approved'];

function errorText(error) {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : 'Could not find that application';
}

export default function ScholarshipStatusPage() {
  const [params] = useSearchParams();
  const [number, setNumber] = useState(params.get('number') || '');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const lookup = async (value) => {
    const query = (value || '').trim().toUpperCase();
    if (!query) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const response = await axios.get(`${API}/scholarship/track/${encodeURIComponent(query)}`);
      setResult(response.data);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get('number')) lookup(params.get('number'));
  }, []);

  const activeIndex = result?.status === 'rejected' ? -1 : STEPS.indexOf(result?.status);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-semibold text-gray-900">Track scholarship status</h1>
        <p className="mt-2 text-sm text-gray-600">Enter the application number from your confirmation email or SMS.</p>
        <form
          className="mt-6 flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            lookup(number);
          }}
        >
          <Input
            value={number}
            onChange={(event) => setNumber(event.target.value)}
            placeholder="SCH2026-0001"
            data-testid="scholarship-status-number"
          />
          <Button type="submit" disabled={loading} className="bg-[#A89968] hover:bg-[#8f8356] text-white">
            {loading ? 'Checking…' : 'Check'}
          </Button>
        </form>
        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
        {result && (
          <div className="mt-6 rounded-xl bg-white p-5 shadow-sm border border-gray-100" data-testid="scholarship-status-result">
            <p className="text-sm text-gray-500">{result.application_number}</p>
            <p className="text-2xl font-semibold text-gray-900">{result.status_label}</p>
            {result.status === 'rejected' ? (
              <p className="mt-3 text-sm text-red-700">This application was rejected.</p>
            ) : (
              <ol className="mt-4 flex gap-2 text-xs">
                {['Submitted', 'Under Review', 'Approved'].map((label, index) => (
                  <li
                    key={label}
                    className={`flex-1 rounded-md px-2 py-2 text-center ${index <= activeIndex ? 'bg-[#A89968] text-white' : 'bg-gray-100 text-gray-600'}`}
                  >
                    {label}
                  </li>
                ))}
              </ol>
            )}
            <p className="mt-4 text-sm text-gray-700">
              Completed rentals: {result.rental_count}. Book review: {result.review_submitted ? 'submitted' : 'missing'}.
            </p>
            {!result.rental_completed && result.status !== 'rejected' && (
              <p className="mt-2 text-sm text-amber-800">
                Final approval stays blocked until at least one book rental is completed.
              </p>
            )}
            {result.disbursement_paused && (
              <p className="mt-2 text-sm text-amber-800">
                Scholarship disbursement is paused until the utilization report for {result.missing_utilization_period} is submitted.
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
